import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

// ─────────────────────────────────────────────────────────────
// FormModal — polished modal shell for forms.
//   • blurred backdrop, centered card, scrollable body, sticky footer
//   • header = optional icon badge + title (no subtitle/captions)
//   • footer = caller-supplied action buttons (no timestamps)
// Intentionally text-light: only the title + the fields the caller passes.
// ─────────────────────────────────────────────────────────────

interface FormModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  icon?: ReactNode;
  headerRight?: ReactNode;
  footer: ReactNode;
  children: ReactNode;
}

export function FormModal({ open, onClose, title, icon, headerRight, footer, children }: FormModalProps) {
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        background: 'rgba(15,23,32,0.45)',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '40px 16px',
        overflowY: 'auto',
      }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 680,
          background: 'var(--color-panel)',
          borderRadius: 14,
          boxShadow: '0 24px 60px rgba(15,23,32,0.28)',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100vh - 80px)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            padding: '18px 22px',
            borderBottom: '1px solid var(--color-border)',
            flexShrink: 0,
          }}
        >
          {icon && (
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 9,
                background: 'rgba(87,154,59,0.10)',
                color: 'var(--color-accent)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {icon}
            </div>
          )}
          <h2
            style={{
              flex: 1,
              minWidth: 0,
              fontFamily: 'var(--font-display)',
              fontSize: 21,
              fontWeight: 600,
              letterSpacing: -0.3,
              color: 'var(--color-text)',
              margin: 0,
            }}
          >
            {title}
          </h2>
          {headerRight}
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 30,
              height: 30,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 7,
              border: 'none',
              background: 'transparent',
              color: 'var(--color-text-dim)',
              cursor: 'pointer',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-panel-alt)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            background: 'var(--color-panel)',
            padding: '20px 22px 22px',
            display: 'flex',
            flexDirection: 'column',
            gap: 22,
          }}
        >
          {children}
        </div>

        {/* Sticky footer */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
            padding: '14px 22px',
            borderTop: '1px solid var(--color-border)',
            background: 'var(--color-panel-alt)',
            flexShrink: 0,
          }}
        >
          {footer}
        </div>
      </div>
    </div>,
    document.body,
  );
}
